import * as React from "react";
import * as S from "@styled";
import * as ChangeProcessListPage from "../../processList/actions/ChangeProcessListPage";
import * as Actions from "../../processList/actions/ProcessListActions";
import * as AppPath from "../../processList/model/AppPath";
import { flexRender, getCoreRowModel, useReactTable, SortingState, ColumnSort } from '@tanstack/react-table'
import { useQuery } from "shared/hooks/useQueryParams";
import { connect } from "react-redux";
import { IProcessListTableDispatchProps, IProcessListTableProps, IProcessListTableStateProps } from "shared/model/Table";
import { State } from "processList/model/State";
import { DispatchType, Utilities } from "shared/utilities";
import { Option } from "shared/model/Option";
import { ProcessColumnIdentifier } from "processList/model/ProcessList";
import { getRowsPerPage } from "processList/model/RowsPerPage";
import { SortByExeutionColumns } from "processList/model/ListState";
import { TableBottomControlItem, TableBottomControlsContainer, TableModeSelect, TablePagination, getTableModeSelectOptions } from "shared/components/Table";

const ProcessListTable: React.FC<IProcessListTableProps> = (props) => {

    const pageSize = getRowsPerPage();
    const query = useQuery();
    const pagination = React.useMemo(
        () => ({
            pageIndex: props.activePage,
            pageSize: pageSize,
        }),
        [props.activePage, pageSize]
    );

    const desc = query.get("desc");
    const sortBy = query.get("sortBy");

    const disablePaginationForSort = sortBy && Object.values(SortByExeutionColumns).includes(sortBy as SortByExeutionColumns) || false;

    const getSortingStateFromUrl = (): ColumnSort => ({
        id: sortBy ?? ProcessColumnIdentifier.name,
        desc: desc == null ? false : desc?.toLowerCase() === "true"
    });

    const sortedStateFromUrl = getSortingStateFromUrl();

    const sorting = React.useMemo<SortingState>(
        () => ([
            {
                desc: sortedStateFromUrl.desc,
                id: sortedStateFromUrl.id
            }
        ]),
        [sortedStateFromUrl]
    );

    const visibleColumns = new Map(props.visibleColumns.map((col) => [col.valueOf(), true]));
    const possibleColumns = new Map(props.possibleColumns.map((col) => [col.column.valueOf(), false]));

    const columnVisibility = new Map([...possibleColumns, ...visibleColumns]);
    const selectedProcesses = new Map(props.selectedProcesses.map((processId) => [processId.valueOf(), true]));

    const table = useReactTable({
        data: props.data,
        columns: props.columns,
        pageCount: props.pageCount ?? -1,
        state: {
            rowSelection: Object.fromEntries(selectedProcesses),
            columnVisibility: Object.fromEntries(columnVisibility),
            pagination,
            sorting
        },
        getCoreRowModel: getCoreRowModel(),
        manualPagination: true,
        manualSorting: true,
        enableSortingRemoval: false
    });

    const handleSortClick = (id: string) => {
        let descending = sorting[0].id !== id ? sorting[0].desc : !sorting[0].desc;
        // No descending for execution columns
        if (descending && Object.values(SortByExeutionColumns).includes(id as SortByExeutionColumns)) {
            // Do we need some better way of indicating this for the user than console
            console.log("No descending sorting for execution columns.")
            descending = false;
        }

        // Keep sort order the same as preivous selection if going to next category. 
        AppPath.replaceSearchParameters(AppPath.getNewUrlState({ desc: descending, sortBy: id, page: null }));
    }

    const handlePageClick = (event: { selected: number }) => {
        AppPath.replaceSearchParameters(AppPath.getNewUrlState({ page: event.selected + 1 }));
    };

    const handleRowsPerPage = (newPerRow: Option<number>) => {
        AppPath.replaceSearchParameters(AppPath.getNewUrlState({ pageSize: newPerRow.value, page: null }));
        props.onRowsPerPageChange(newPerRow.value)
    };

    const handleExecutionCountLimit = (newLimit: Option) => {
        props.onChangeCountsTimeLimitDays(newLimit.value)
    }

    const rowsPerPageOptions = getTableModeSelectOptions([10, 15, 25, 50, 100], [{ label: FRENDS.Localizations.Unlimited, value: Utilities.unlimitedPageSize }]);

    const executionCountLimitOptions = getTableModeSelectOptions([1, 7, 30], [{ label: FRENDS.Localizations.CountLimit_All, value: 0 }]);

    return (
        <>
            <S.ResponsiveTableContainer style={{ minWidth: 'fit-content' }}>
                <S.StyledProcessListTable gridTemplateColumns={`60px repeat(${props.visibleColumns.length + 1}, auto)`}>
                    {/* ^ Fixed 60px is  select checkbox and 1 in the end includes row selector and column visibility adjuster / row actions */}
                    <thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    return (
                                        <th key={header.id} colSpan={header.colSpan} id={`header-${header.column.id}-selector`}>

                                            {header.isPlaceholder && null}

                                            {!header.isPlaceholder && header.column.getCanSort() && (
                                                <div
                                                    {...{
                                                        className: 'pointer',
                                                        onClick: () => handleSortClick(header.column.id)
                                                    }}
                                                >
                                                    {flexRender(
                                                        header.column.columnDef.header,
                                                        header.getContext()
                                                    )}
                                                </div>
                                            )}

                                            {!header.isPlaceholder && !header.column.getCanSort() && (
                                                <>
                                                    {flexRender(
                                                        header.column.columnDef.header,
                                                        header.getContext()
                                                    )}
                                                </>
                                            )}
                                        </th>
                                    )
                                })}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.map((row) => {
                            return (
                                <tr key={row.id} data-highlighted={query.get("highlightProcess") === row.original.id?.toString()}>
                                    {row.getVisibleCells().map((cell) => {
                                        return (
                                            <td key={cell.id}>
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext()
                                                )}
                                            </td>
                                        )
                                    })}
                                </tr>
                            )
                        })}
                    </tbody>
                </S.StyledProcessListTable>
            </S.ResponsiveTableContainer>

            <TableBottomControlsContainer>
                <TableBottomControlItem justifyContent="start">
                    {table.getPageCount() > 1 && (
                        <TablePagination
                            forcePage={table.getState().pagination.pageIndex - 1}
                            pageCount={table.getPageCount()}
                            onPageChange={handlePageClick}
                            disabled={disablePaginationForSort}
                        />
                    )}
                    {disablePaginationForSort ? "Pagination disabled when sorting by Executions" : null}
                </TableBottomControlItem >
                <TableBottomControlItem >
                    <TableModeSelect
                        id="rows-per-page-selector"
                        firstLabel="Show"
                        secondLabel={props.totalCountLabel}
                        options={rowsPerPageOptions}
                        value={Utilities.valueFromId(rowsPerPageOptions, table.getState().pagination.pageSize)}
                        onChange={handleRowsPerPage}
                        menuPlacement="auto"
                    />
                </TableBottomControlItem >
                <TableBottomControlItem justifyContent="flex-end" >
                    <TableModeSelect
                        id="execution-counts-selector"
                        firstLabel={FRENDS.Localizations.GetExecutionCountsForLast}
                        secondLabel={FRENDS.Localizations.CountLimit_Days}
                        options={executionCountLimitOptions}
                        value={Utilities.valueFromId(executionCountLimitOptions, props.countsTimeLimitDays)}
                        onChange={handleExecutionCountLimit}
                        menuPlacement="auto"
                    />
                </TableBottomControlItem>
            </TableBottomControlsContainer>
        </>
    )
}

const mapProcessListTableStateToProps = (state: State): IProcessListTableStateProps => {
    return {
        countsTimeLimitDays: state.listState.countsTimeLimitDays,
        listState: state.listState,
        loading: state.loading
    };
};

const mapProcessListTableDispatchToProps = (dispatch: DispatchType<State>): IProcessListTableDispatchProps => {
    return {
        onRowsPerPageChange: (rows: number) => dispatch(ChangeProcessListPage.changeProcessListRowsPerPage(rows)),
        onChangeCountsTimeLimitDays: (newLimit: number) => dispatch(Actions.changeCountsTimeLimitDays(newLimit)),
    };
};

export default connect(mapProcessListTableStateToProps, mapProcessListTableDispatchToProps)(ProcessListTable);
